import { FullDashboard } from "lib/entries/types";

import React from "react";
import { ButtonOld } from "components/ButtonOld/ButtonOld";
import { ButtonGroup } from "components/ButtonOld/ButtonGroup";
import { Dialog } from "gui/dialog/dialog";
import { FieldButtons } from "gui/input/fields";
import { FieldError } from "gui/input/fields";
import { FieldLabel } from "gui/input/fields";
import { Fields } from "gui/input/fields";
import { InputText } from "gui/input/input";
import { generateSlug } from "lib/links/slugs";
import { logger } from "lib/logger/browser";
import { useRouter } from "next/router";
import { validateSlug } from "lib/links/slugs";

import { gql } from "@apollo/client";
import { useForkDashboardMutation } from "lib/types/graphql";
import { useSession } from "gui/session/session";
import { Session } from "lib/users/types";
import { useActiveContext, ActiveContext } from "shared/ContextSwitcher/store";
import { pick } from "lodash";

import styles from "./dashboard.module.css";
import { IconButton } from "components/Button/IconButton";
import { IconGitFork } from "components/Icons/IconGitFork";
import { Tooltip } from "components/Tooltip/Tooltip";

interface Props {
  dashboard: Pick<FullDashboard, "id" | "name">;
}

export function ForkButton(props: Props) {
  const session = useSession();
  const activeContext = useActiveContext();

  const [
    isForkDashboardDialogOpen,
    setIsForkDashboardDialogOpen,
  ] = React.useState(false);

  if (!session || !session.user || !activeContext) {
    return null;
  }

  const onDialog = () => setIsForkDashboardDialogOpen((prev) => !prev);

  return (
    <>
      <Tooltip position="below-center" label="Fork">
        <IconButton
          onClick={onDialog}
          size="M"
          className={styles.alignMiddle}
          theme="tertiary"
          aria-label="Fork"
        >
          <IconGitFork />
        </IconButton>
      </Tooltip>
      <Dialog
        label="New dashboard"
        size="sm"
        isOpen={isForkDashboardDialogOpen}
        onDismiss={onDialog}
      >
        <ForkDashboardDialog
          session={session}
          activeContext={activeContext}
          dashboard={props.dashboard}
          close={onDialog}
        />
      </Dialog>
    </>
  );
}

function ForkDashboardDialog(props: {
  session: Session;
  activeContext: ActiveContext;
  dashboard: Pick<FullDashboard, "id" | "name">;
  close: () => void;
}) {
  const [name, setName] = React.useState("");
  const [customSlug, setCustomSlug] = React.useState("");
  const [error, setError] = React.useState<Error | null>(null);
  const { session, activeContext } = props;
  const router = useRouter();

  const slug = React.useMemo(() => customSlug.trim() || generateSlug(name), [
    customSlug,
    name,
  ]);
  const url = React.useMemo(
    () => `https://dune.com/${activeContext.handle}/${slug}`,
    [slug]
  );

  const [forkDashboard, result] = useForkDashboardMutation({
    context: { session },
    variables: {
      existing_dashboard_id: props.dashboard.id,
      new_dashboard_name: name,
      new_dashboard_slug: slug,
      contextOwner: pick(activeContext, ["id", "type"]),
    },
  });
  const isLoading = result.loading !== false || result.data !== undefined;

  const onSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (!name || !name.trim()) {
      // Name must not be empty; error and abort
      setError(Error("Please enter a dashboard name"));
      return;
    } else if (!validateSlug(slug)) {
      setError(
        Error(
          "The URL you entered is not valid. It can only contain lowercase letters, numbers and dashes (-)"
        )
      );
      return;
    }

    setError(null);
    forkDashboard()
      .then((result) => {
        if (result.data && result.data.fork_dashboard_v2) {
          const { slug } = result.data.fork_dashboard_v2;
          router.push(`/${activeContext.handle}/${slug}`);
        } else {
          logger.error(
            "Expected data to be returned when forking dashboard but got:",
            result
          );
        }
      })
      .catch((err) => {
        logger.error(err);
      });
  };

  React.useEffect(() => {
    if (result.error) {
      if (
        result.error.message.includes("dashboards_unique_user_slug_idx") ||
        result.error.message.includes("dashboards_unique_team_slug_idx")
      ) {
        setError(
          Error(
            customSlug.trim()
              ? "You already have a dashboard with this URL. Please pick another one."
              : "You already have a dashboard with this name. Please pick another one."
          )
        );
      } else if (privateDashboardNotAllowed(result.error.message)) {
        setError(Error(result.error.message));
      } else {
        setError(Error("Something went wrong"));
      }
    }
  }, [result.error]);

  return (
    <form onSubmit={onSubmit}>
      <Fields>
        <FieldLabel label="Dashboard name">
          <div className={styles.dashboardName}>
            <InputText
              placeholder="My dashboard"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <p className={styles.linkPreview} title={url}>
              {url}
            </p>
          </div>
        </FieldLabel>
        <FieldLabel label="Customize the URL">
          <InputText
            placeholder={slug || "my-dashboard"}
            value={customSlug}
            onChange={(event) => setCustomSlug(event.target.value)}
          />
        </FieldLabel>

        <FieldButtons>
          <ButtonGroup>
            <ButtonOld loading={isLoading} type="submit" size="sm" color2>
              {isLoading ? "Saving..." : "Save and open"}
            </ButtonOld>
            <ButtonOld
              onClick={props.close}
              loading={isLoading}
              size="sm"
              color2
              light
            >
              Cancel
            </ButtonOld>
          </ButtonGroup>
        </FieldButtons>

        {error && !privateDashboardNotAllowed(error.message) && (
          <FieldError error={error} />
        )}
      </Fields>
    </form>
  );
}

gql`
  mutation ForkDashboard(
    $existing_dashboard_id: Int!
    $new_dashboard_name: String!
    $new_dashboard_slug: String!
    $contextOwner: ContextOwner!
  ) {
    fork_dashboard_v2(
      dashboard_id: $existing_dashboard_id
      dashboard_name: $new_dashboard_name
      dashboard_slug: $new_dashboard_slug
      forkTo: $contextOwner
    ) {
      slug
    }
  }
`;

const privateDashboardNotAllowed = (errorMessage: string): boolean => {
  return (
    errorMessage.includes("max_number_of_private_dashboards_reached") ||
    errorMessage.includes("Reached max number of private dashboards.")
  );
};
